import React from 'react'
import { useNavigate } from 'react-router-dom'
import Tooltip from '@mui/material/Tooltip'
import {
    changeDateFormat,
    getSampleActivityStatus,
    onSetSampleDetailsBreadcrumb
} from '../../helpers/Util'
import { addMixpanelTrackingEvent } from '../../services/CommonService'

const STATUS_CONFIG = {
    RUNNING: { label: 'Running', className: 'order-status-badge running' },
    PENDING: { label: 'Pending', className: 'order-status-badge pending' },
    COMPLETED: {
        label: 'Completed',
        className: 'order-status-badge completed'
    },
    CANCELLED: { label: 'Cancelled', className: 'order-status-badge cancelled' }
}

/**
 * Map sample internal API status to the UI status key.
 * Running  → internal PENDING
 * Pending  → internal INITIALIZED
 * Completed → internal COMPLETED
 * Cancelled → internal INITIALIZED + approvalStatus CANCELLED
 */
const getItemStatus = (item, activeTab) => {
    if (activeTab && activeTab !== 'ALL') return activeTab
    const s = item?.status?.toUpperCase()
    const approval = item?.approvalStatus?.toUpperCase()
    if (!s) return 'RUNNING'
    if (s === 'COMPLETED') return 'COMPLETED'
    if (s === 'PENDING') return 'RUNNING'
    if (s === 'INITIALIZED') {
        if (approval === 'CANCELLED') return 'CANCELLED'
        return 'PENDING'
    }
    return 'RUNNING'
}

/**
 * Map activityCountList display names to the type codes used by getSampleActivityStatus.
 */
const ACTIVITY_NAME_TO_TYPE = {
    'Sample Requested': 'SAMPLE_REQUESTED',
    Pattern: 'PATTERN',
    Cutting: 'CUTTING',
    Sewing: 'SEWING',
    Embroidery: 'EMBROIDERY',
    Print: 'PRINT',
    Wash: 'WASH',
    Delivered: 'DELIVERED',
    'Handover to Factory': 'HANDOVER_TO_FACTORY',
    'Received from Factory': 'RECEIVED_FROM_FACTORY',
    'Send to Buyer': 'SEND_TO_BUYER',
    'Received by Buyer': 'RECEIVED_BY_BUYER',
    Approved: 'APPROVED',
    Rejected: 'REJECTED'
}

/**
 * Derive current stage from activityCountList.
 * Returns { name, typeCode } for the last activity with count > 0,
 * falling back to the first activity.
 */
const getStage = (activityCountList) => {
    if (!Array.isArray(activityCountList) || activityCountList.length === 0)
        return { name: '—', typeCode: null, count: 0 }
    let last = null
    for (const act of activityCountList) {
        if (act.count > 0) last = act
    }
    const entry = last || activityCountList[0]
    const name = entry?.activity || '—'
    return {
        name,
        typeCode: ACTIVITY_NAME_TO_TYPE[name] || null,
        count: entry?.count ?? 0
    }
}

const SampleTableView = ({ sampleStore, activeTab }) => {
    const navigate = useNavigate()
    const samples = sampleStore?.samples?.data ?? []

    const handleRoute = (sample) => {
        onSetSampleDetailsBreadcrumb(sample, activeTab, (url) => {
            addMixpanelTrackingEvent({}, 'BUYER_SAMPLE_ON_CLICK')
            navigate(url)
        })
    }

    if (!samples.length) return null

    return (
        <div className='order-table-view mt-[14px]'>
            <div className='overflow-x-auto'>
                <table className='table order-list-table w-full'>
                    <thead>
                        <tr>
                            <th>Ref</th>
                            <th>Style</th>
                            <th className='text-right'>Styles</th>
                            <th>Stage</th>
                            <th>Expected By</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {samples.map((sample, index) => {
                            const status = getItemStatus(sample, activeTab)
                            const statusCfg =
                                STATUS_CONFIG[status] ||
                                STATUS_CONFIG['RUNNING']
                            const stage = getStage(sample?.activityCountList)
                            const stageColorClass = stage.typeCode
                                ? getSampleActivityStatus(stage.typeCode)
                                : ''
                            return (
                                <tr
                                    key={`sample_table_${sample.id || index}`}
                                    className='cursor-pointer'
                                    onClick={() => handleRoute(sample)}
                                >
                                    <td>
                                        <span className='regular-14 weight-500 black'>
                                            {sample.refNo || '—'}
                                        </span>
                                    </td>
                                    <td>
                                        <Tooltip
                                            title={sample.title || ''}
                                            placement='top'
                                            arrow
                                        >
                                            <span className='regular-14 black'>
                                                {sample.title?.length > 40
                                                    ? `${sample.title.slice(
                                                          0,
                                                          40
                                                      )}…`
                                                    : sample.title || '—'}
                                            </span>
                                        </Tooltip>
                                    </td>
                                    <td className='text-right'>
                                        <span className='regular-14 black'>
                                            {sample.noOfDesign ?? '—'}
                                        </span>
                                    </td>
                                    <td>
                                        <span
                                            className={`status ${stageColorClass}`}
                                        >
                                            {stage.name}
                                            {stage.count > 0 && (
                                                <span className='badge-count ml-1'>
                                                    ({stage.count})
                                                </span>
                                            )}
                                        </span>
                                    </td>
                                    <td>
                                        <span className='regular-14 gray_300'>
                                            {sample.requireDate
                                                ? changeDateFormat(
                                                      sample.requireDate,
                                                      'YYYY-MM-DD',
                                                      'DD MMM, YYYY'
                                                  )
                                                : '—'}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={statusCfg.className}>
                                            {statusCfg.label}
                                        </span>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default SampleTableView
